/**
 * Handmatig ingevulde uren bij verlof en ziekte — het totaal uit Numbers, verdeeld over de
 * weken. De bestandsnaam stamt uit de tijd dat alleen verlof uren droeg.
 *
 * Een afwezigheidsrecord van een type uit TYPEN_MET_UREN ('verlof' en 'ziekte') mag een veld
 * `uren` dragen: het TOTAAL voor de hele periode van dat record, overgenomen uit Numbers.
 * Staat dat veld er niet, dan verandert er niets — dan geldt onverkort het oude pad (8u per
 * would-be-werkdag, mét de aftopping op het blok-gat). Een 'anders'-record draagt nooit uren.
 *
 * DRIE OPSLAGVORMEN, alle drie geldig — zelfde afspraak als bij de kantooruren
 * (lib/roostercodes.js:361-398):
 *   { naam, type:'ziekte', vanDatum, totDatum }            de oude vorm — 8u per werkdag
 *   { naam, type:'ziekte', vanDatum, totDatum, uren: 26 }  sinds het totaal invulbaar werd
 *   { ..., uren: 48, urenPerWeek: { '2026-44': 30, '2026-45': 18 } }
 *                                                          met handmatige bedragen per ISO-week
 * Er wordt NIETS gemigreerd. ingevuldeUrenVanRecord is daarom bewust tolerant: elke vorm die
 * geen bruikbaar getal oplevert geeft null, en dan valt elke lezer terug op het oude pad.
 *
 * ── WAAROM DIT EEN APART BESTAND IS ─────────────────────────────────────────
 * Niet in data/afwezigheid.js: dit bestand heeft vakantieDagenInWeek nodig en
 * data/vakanties.js:10 importeert zelf al uit data/afwezigheid.js. De helper daar neerzetten
 * zou een import-cycle geven (afwezigheid -> vakanties -> afwezigheid).
 * Niet in lib/: engine/blok-generator.js moet hem kunnen aanroepen, en engine/ mag nooit uit
 * lib/ importeren — de deploy-zip bevat alleen engine/ en data/ (zie lib/balans.js:32-34).
 * data/ mag wel uit engine/ importeren; de afhankelijkheden hieronder zijn alle vier bladeren
 * of wijzen van data/ naar engine/, dus er ontstaat geen cycle:
 *     verlofuren -> rooster-engine (importeert zelf niets)
 *     verlofuren -> afwezigheid    (importeert zelf niets)
 *     verlofuren -> vakanties      -> afwezigheid
 *     verlofuren -> maand          (importeert zelf niets)
 *
 * ── DE ENGINE REKENT HIER NIETS VAN ─────────────────────────────────────────
 * De engine krijgt de afwezigheidslijst nooit te zien: hij werkt met platgeslagen per-week
 * maps (afwMaps, afwTypeMaps) waarin `uren`, `vanDatum` en `totDatum` al verdwenen zijn. Hij
 * heeft bovendien geen vakanties-lijst en kan er geen bouwen. Daarom levert dit bestand een
 * DERDE per-week map, via exact dezelfde route als afwTypeMaps:
 *     afwUrenMaps  { [weeknummer]: { naam: { perType: { ziekte: 13 }, dagen: { ma: 'ziekte' } } } }
 * `perType` is het TOTAAL voor DEZE week per type; `dagen` zegt welke dagen door een
 * uren-record gedekt zijn en met welk type. Geen enkele lezer heeft een losse dagwaarde nodig:
 * engine en balans tellen de weektotalen op, en de dag dient alleen om te weten dat hij al
 * betaald is en dus geen 8u credit via het oude pad meer hoort te krijgen.
 *
 * ── HOE HET TOTAAL OVER DE WEKEN GAAT (§54f, portie 2) ──────────────────────
 * Wat Numbers PER DAG rekent is van buitenaf niet te reconstrueren: Emre (32u) kost 8,00u per
 * losse dag, Leroy (28u) 5,60u, en Numbers weet niet wie wanneer werkt. Wat wel vaststaat: een
 * VOLLE week kost nooit meer dan het weekcontract. Daarom krijgt elke ISO-week die een record
 * raakt een GEWICHT, en bepalen die gewichten alleen de VERHOUDING tussen de weken:
 *     gewicht = min(8 × gedekte werkdagen ma-vr, contract_uren)     za en zo tellen niet mee
 * Het ingevulde totaal blijft leidend: de weken samen krijgen er precies dat getal uit. Wie op een
 * zaterdag of zondag werkt, heeft dat in Numbers al zelf bijgevuld; dat zit dus in het totaal.
 *     Emre, 24 okt t/m 3 nov, 48u: gewichten 0 (za+zo) : 32 (ma-vr) : 16 (ma+di) -> 0 / 32 / 16
 * Elke week behalve de laatste met een gewicht krijgt hele uren (naar beneden afgerond); die
 * laatste krijgt het restant. Zo is de som altijd bit-exact het ingevulde getal, en kan alleen die
 * ene week decimalen dragen — de optelling over losse dagwaarden uit §55f komt niet meer voor.
 * Zijn ALLE gewichten 0 (een puur weekendrecord, of iemand zonder contract), dan gaat het hele
 * totaal naar de laatste gedekte week. Een ingevuld getal valt nooit stil weg.
 *
 * ── HANDMATIGE WEEKBEDRAGEN ─────────────────────────────────────────────────
 * Een record mag naast `uren` ook `urenPerWeek` dragen: per ISO-week een bedrag, met dezelfde
 * sleutels als perWeek (`${isoJaar}-${week}`). Is dat veld te vertrouwen, dan gaan die bedragen
 * letterlijk naar de weken en speelt het gewicht geen rol; dan zijn de weekbedragen leidend en
 * niet het totaal, en mogen ze er samen hoogstens 0,01 van afwijken. Is het niet te vertrouwen,
 * dan geldt de verdeling hierboven, precies alsof het veld er niet stond. Wanneer het te
 * vertrouwen is: zie handmatigeWeekdelen. Binnen een week verandert er niets: ook een handmatig
 * bedrag gaat via dagwaardenVanWeek over de dagen.
 *
 * DE TYPE-CONTROLE ZIT HIER, NIET BIJ DE LEZER. Liggen er twee records op dezelfde dag, dan
 * kiest de typeMap het laatste record en deze map het laatste record MET uren; dat kunnen
 * verschillende records zijn. Een dag telt daarom alleen mee als de typeMap hem aan hetzelfde
 * type toeschrijft als het bronrecord. Die controle stond woordelijk in de engine én in
 * balans.js; sinds de map weektotalen levert kan ze daar niet meer staan en staat ze op de
 * enige plek die de rauwe records nog ziet. Een kaal getal — de vorm van vóór het type erbij
 * kwam — heeft geen type en komt hier dus nooit door.
 */

import { wouldBeWerkdag, schoolweekType } from '../engine/rooster-engine.js';
import { berekenAfwezigheidDagen, bouwAfwezigheidTypeMap, ALLE_DAGEN } from './afwezigheid.js';
import { vakantieDagenInWeek } from './vakanties.js';
import { isoWeekNummer, weekNaarMaandag } from './maand.js';

/**
 * De afwezigheidstypen waarvan een record een ingevuld urentotaal mag (en in het formulier:
 * moet) dragen. Eén lijst voor de leeslaag, het formulier en het overzicht in Beheer.
 */
export const TYPEN_MET_UREN = ['verlof', 'ziekte'];

/**
 * Het ingevulde totaal van een record, of null.
 *
 * Tolerant, precies zoals overrideUren (lib/roostercodes.js:395-398): alles wat geen
 * bruikbaar getal is geeft null, en dan valt de aanroeper terug op het oude pad. Er is
 * bewust GEEN bereikcontrole — net als bij de kantooruren is het formulier daar de
 * poort voor, niet de leeslaag. Een record uit een oudere back-up (zonder `uren`) en een
 * record met een onzinnige waarde komen hier dus allebei als null uit.
 *
 * ALLEEN de typen uit TYPEN_MET_UREN. Een `uren`-veld dat per ongeluk op 'anders' staat
 * wordt hier al genegeerd, zodat geen enkele lezer verderop dat onderscheid nog hoeft te maken.
 */
export function ingevuldeUrenVanRecord(record) {
  if (!record || !TYPEN_MET_UREN.includes(record.type)) return null;
  if (!Number.isFinite(record.uren)) return null;
  return record.uren;
}

/** Het medewerker-object bij een weergavenaam, of null. Meta-sleutels (_uitleg) overslaan. */
function medewerkerOpNaam(regels, naam) {
  for (const [key, mw] of Object.entries(regels?.medewerkers || {})) {
    if (key.startsWith('_')) continue;
    if (mw && typeof mw === 'object' && mw.naam === naam) return mw;
  }
  return null;
}

/**
 * De would-be-werkdagen van de HELE periode van één record, chronologisch.
 *
 * Loopt per ISO-WEEK, niet per kalenderdag, en gebruikt binnen elke week dezelfde
 * berekenAfwezigheidDagen als bouwAfwezigheidMap (data/afwezigheid.js:128). Dat is bewust:
 * zo kan de dag-toewijzing per week NOOIT uiteenlopen met de afwezigheidsmap waar de engine
 * en balans.js mee werken. Zou dit per kalenderdag rekenen met een eigen week-afleiding, dan
 * kon een dag op een weekgrens in de ene telling wél en in de andere niet meedoen.
 *
 * De volgorde is die van de engine: weken oplopend, binnen een week ALLE_DAGEN-volgorde —
 * dezelfde volgorde als lib/balans.js:198-201 en engine/rooster-engine.js:1536-1539.
 *
 * @returns {Array<{isoJaar:number, week:number, dag:string}>} leeg als er geen werkdag is
 */
function werkdagenInPeriode(record, mw, regels, vakanties) {
  const van = new Date(record.vanDatum + 'T00:00:00');
  const tot = new Date(record.totDatum + 'T00:00:00');
  if (Number.isNaN(van.getTime()) || Number.isNaN(tot.getTime()) || van > tot) return [];

  const uit = [];
  // Start op de MAANDAG van de week waarin vanDatum valt, en stap per week door tot voorbij
  // totDatum. De maandag van de laatste week ligt per definitie op of vóór totDatum, dus de
  // laatste week doet altijd mee.
  const eerste = isoWeekNummer(van);
  const cursor = weekNaarMaandag(eerste.week, eerste.isoJaar);
  while (cursor <= tot) {
    const { week, isoJaar } = isoWeekNummer(cursor);
    const dagenInWeek = berekenAfwezigheidDagen(record.vanDatum, record.totDatum, week, isoJaar);
    if (dagenInWeek.length) {
      const weekType = schoolweekType(week);
      const vakDagen = new Set(vakantieDagenInWeek(week, isoJaar, vakanties));
      for (const dag of ALLE_DAGEN) {
        if (!dagenInWeek.includes(dag)) continue;
        if (!wouldBeWerkdag(mw, dag, weekType, week, regels, vakDagen)) continue;
        uit.push({ isoJaar, week, dag });
      }
    }
    cursor.setDate(cursor.getDate() + 7);
  }
  return uit;
}

/**
 * Hoeveel would-be-werkdagen telt de periode van dit record?
 *
 * Voor de hulpregel in het formulier, en daarom LOS van het `uren`-veld: het formulier moet dit
 * getal al kunnen tonen vóór er iets ingevuld is ("5 werkdagen in deze periode"). Daarom
 * accepteert deze functie een kaal { naam, vanDatum, totDatum } — een type of een urental is
 * niet nodig.
 *
 * Tot portie 2 was dit ook de deler van verdeelIngevuldeUren. Dat is het niet meer: die verdeelt
 * per week naar een gewicht dat niet van would-be-werkdagen afhangt (zie de kop van dit bestand).
 *
 * 0 zodra er niets te tellen valt: onbekende medewerker, ontbrekende of omgekeerde datums,
 * of een periode die alleen op structureel vrije dagen valt.
 */
export function telWerkdagenInPeriode(record, regels, vakanties = []) {
  if (!record?.naam || !record?.vanDatum || !record?.totDatum) return 0;
  const mw = medewerkerOpNaam(regels, record.naam);
  if (!mw) return 0;
  return werkdagenInPeriode(record, mw, regels, vakanties).length;
}

/** De dagen die in het weekgewicht meetellen. Zaterdag en zondag zet Numbers op 0. */
const WERKWEEK = ['ma', 'di', 'wo', 'do', 'vr'];
const UREN_PER_WERKDAG = 8;

/**
 * De ISO-weken die de periode van één record raakt, chronologisch, met per week de gedekte
 * KALENDERdagen in ALLE_DAGEN-volgorde.
 *
 * Dezelfde weekafleiding als werkdagenInPeriode — per ISO-week, met dezelfde
 * berekenAfwezigheidDagen als bouwAfwezigheidMap — maar zonder de would-be-filter: Numbers weet
 * niet wie wanneer werkt, dus het weekgewicht hangt daar ook niet van af.
 *
 * @returns {Array<{isoJaar:number, week:number, dagen:string[]}>} leeg bij ontbrekende of
 *          omgekeerde datums
 */
function gedekteWeken(record) {
  const van = new Date(record.vanDatum + 'T00:00:00');
  const tot = new Date(record.totDatum + 'T00:00:00');
  if (Number.isNaN(van.getTime()) || Number.isNaN(tot.getTime()) || van > tot) return [];

  const uit = [];
  const eerste = isoWeekNummer(van);
  const cursor = weekNaarMaandag(eerste.week, eerste.isoJaar);
  while (cursor <= tot) {
    const { week, isoJaar } = isoWeekNummer(cursor);
    const dagenInWeek = berekenAfwezigheidDagen(record.vanDatum, record.totDatum, week, isoJaar);
    if (dagenInWeek.length) uit.push({ isoJaar, week, dagen: ALLE_DAGEN.filter((dag) => dagenInWeek.includes(dag)) });
    cursor.setDate(cursor.getDate() + 7);
  }
  return uit;
}

/**
 * Verdeelt `totaal` over de weken naar rato van hun gewicht.
 *
 * Elke week behalve de laatste met een gewicht krijgt hele uren, naar beneden afgerond; die
 * laatste krijgt wat er dan nog open staat. Daar is het om te doen:
 *   - de som van de delen is bit-exact `totaal`: de hele delen tellen exact op, en het restant
 *     is precies wat er ontbreekt;
 *   - alleen die laatste week kan decimalen dragen (het formulier laat een totaal als 38,40 toe).
 * Naar beneden en niet naar dichtstbij: dan kan het restant nooit negatief worden.
 *
 * Zijn alle gewichten 0, dan gaat het hele totaal naar de laatste week van de periode.
 */
function verdeelNaarGewicht(totaal, gewichten) {
  const som = gewichten.reduce((s, g) => s + g, 0);
  const delen = gewichten.map(() => 0);
  if (!(som > 0)) {
    delen[delen.length - 1] = totaal;
    return delen;
  }
  let laatste = -1;
  for (let i = 0; i < gewichten.length; i++) if (gewichten[i] > 0) laatste = i;
  let uitgedeeld = 0;
  for (let i = 0; i < gewichten.length; i++) {
    if (i === laatste || !(gewichten[i] > 0)) continue;
    delen[i] = Math.floor((totaal * gewichten[i]) / som);
    uitgedeeld += delen[i];
  }
  delen[laatste] = totaal - uitgedeeld;
  return delen;
}

/**
 * Het weekdeel als dagwaarden over de gedekte dagen van die week.
 *
 * Geen lezer heeft een losse dagwaarde nodig (zie de kop). Ze bestaan alleen voor de regel "per
 * dag wint het laatste record met uren" in bouwAfwezigheidUrenMap. Zonder overlap tellen ze per
 * week precies op tot het weekdeel; met overlap verliest het eerdere record alleen de dagen die
 * het afstaat. Het zijn GEEN Numbers-dagwaarden — die zijn van buitenaf niet te kennen.
 *   - De werkdagen (ma-vr) dragen het deel: 8u per dag in de volgorde ma -> vr zolang het deel
 *     strekt; de laatste gedekte werkdag krijgt wat er overblijft.
 *   - Zaterdag en zondag krijgen 0, maar staan er WEL in. Zo komen ze in `dagen` en krijgen ze
 *     bij de lezer geen 8u via het oude pad: Emres za+zo in wk43 zouden anders 2 × 8u extra geven.
 *   - Heeft de week geen werkdag (een puur weekendrecord), dan staat het hele deel op de laatste
 *     gedekte dag.
 * De optelling per week is exact: hele uren, dan hoogstens één waarde met decimalen, dan nullen.
 */
function dagwaardenVanWeek(deel, dagen) {
  const uit = {};
  for (const dag of dagen) uit[dag] = 0;
  const werk = dagen.filter((dag) => WERKWEEK.includes(dag));
  const dragers = werk.length ? werk : [dagen[dagen.length - 1]];
  let rest = deel;
  for (let i = 0; i < dragers.length; i++) {
    const waarde = i === dragers.length - 1 ? rest : Math.min(UREN_PER_WERKDAG, rest);
    uit[dragers[i]] = waarde;
    rest -= waarde;
  }
  return uit;
}

/**
 * Hoeveel de handmatige weekbedragen samen van `uren` mogen afwijken: 0,01, één eenheid in de
 * tweede decimaal — de precisie van het urenveld in het formulier.
 */
const MARGE_WEEKSOM = 0.01;

/**
 * De handmatige weekbedragen van een record (`urenPerWeek`), in de volgorde van `sleutels`, of
 * null als ze niet te vertrouwen zijn. null betekent voor de aanroeper: verdeel naar rato, alsof
 * het veld er niet stond.
 *
 * DE TERUGVAL. Elk van deze drie is op zichzelf genoeg om het HELE veld te negeren:
 *   1. De sleutels zijn niet PRECIES de gedekte weken van het record (gedekteWeken): er mist er
 *      één, of er staat er één bij die het record niet raakt. Ook een andere schrijfwijze van
 *      dezelfde week ('2026-044', '2026-W44') is een andere sleutel.
 *   2. Een bedrag is geen eindig getal >= 0. Een tekst als '30' telt ook niet.
 *   3. De bedragen wijken samen meer dan 0,01 af van `uren`.
 * Een week met 0 is een geldig bedrag en geen ontbrekende week: die week kost niets, maar zijn
 * dagen zijn wél betaald en krijgen bij de lezer geen 8u via het oude pad (dagwaardenVanWeek).
 *
 * Alles of niets: één verkeerd bedrag en het hele record gaat naar rato. Half handmatig en half
 * naar rato zou weekbedragen opleveren die niemand zo heeft ingevuld.
 *
 * "Meer dan 0,01" is in decimalen bedoeld. In floating point is 1,11 − 1,10 net iets meer dan
 * 0,01 (0,010000000000000009); de speling van 1e-9 houdt zo'n verschil binnen de marge.
 *
 * Geen veld (undefined of null) is geen fout maar de gewone vorm, en geeft ook null.
 */
function handmatigeWeekdelen(urenPerWeek, sleutels, totaal) {
  if (!urenPerWeek || typeof urenPerWeek !== 'object' || Array.isArray(urenPerWeek)) return null;

  const opgegeven = Object.keys(urenPerWeek);
  if (opgegeven.length !== sleutels.length || !sleutels.every((s) => opgegeven.includes(s))) return null;

  const delen = sleutels.map((s) => urenPerWeek[s]);
  if (!delen.every((uren) => Number.isFinite(uren) && uren >= 0)) return null;

  const som = delen.reduce((s, uren) => s + uren, 0);
  if (Math.abs(som - totaal) > MARGE_WEEKSOM + 1e-9) return null;
  return delen;
}

/**
 * Verdeelt het ingevulde totaal van ÉÉN record over de ISO-weken van zijn HELE periode.
 *
 * Draagt het record handmatige weekbedragen die te vertrouwen zijn (`urenPerWeek`, zie
 * handmatigeWeekdelen), dan krijgt elke week letterlijk zijn eigen bedrag. Anders krijgt elke
 * week een gewicht: min(8 × gedekte werkdagen ma-vr, contract_uren); zaterdag en zondag tellen
 * niet mee. Het totaal gaat dan naar rato van die gewichten over de weken (verdeelNaarGewicht).
 * Binnen een week gaat het bedrag in beide gevallen over de gedekte dagen (dagwaardenVanWeek).
 * Waarom juist dit gewicht: zie de kop van dit bestand.
 *
 * DE VERDELING GAAT OVER DE HELE PERIODE, nooit per week opnieuw. Een record over een week- of
 * blokgrens wordt één keer verdeeld; elke week pikt daarna alleen zijn eigen deel op.
 *
 * Een ontbrekend of onbruikbaar contract telt als 0. Dan zijn alle gewichten 0 en gaat het hele
 * totaal naar de laatste gedekte week.
 *
 * @returns {{totaal:number, perWeek:Object}|null}
 *          perWeek is gesleuteld op `${isoJaar}-${week}` met daarin { dag: uren } voor ELKE
 *          gedekte dag van die week, ook als die 0 krijgt. `totaal` is altijd het ingevulde
 *          `uren`, ook als handmatige weekbedragen er tot 0,01 van afwijken. null zodra er niets
 *          te verdelen valt: geen ingevuld getal, geen bekende medewerker, of geen geldige periode.
 */
export function verdeelIngevuldeUren(record, regels) {
  const totaal = ingevuldeUrenVanRecord(record);
  if (totaal === null) return null;

  const mw = medewerkerOpNaam(regels, record?.naam);
  if (!mw) return null;

  const weken = gedekteWeken(record);
  if (weken.length === 0) return null;

  // Eén keer de sleutels: dezelfde lijst controleert urenPerWeek en sleutelt perWeek, dus een
  // handmatig bedrag kan nooit onder een andere week landen dan waarvoor het gecontroleerd is.
  const sleutels = weken.map(({ isoJaar, week }) => `${isoJaar}-${week}`);

  let delen = handmatigeWeekdelen(record.urenPerWeek, sleutels, totaal);
  if (delen === null) {
    const contract = Number.isFinite(mw.contract_uren) ? mw.contract_uren : 0;
    const gewichten = weken.map(({ dagen }) =>
      Math.min(UREN_PER_WERKDAG * dagen.filter((dag) => WERKWEEK.includes(dag)).length, contract));
    delen = verdeelNaarGewicht(totaal, gewichten);
  }

  const perWeek = {};
  weken.forEach(({ dagen }, i) => {
    perWeek[sleutels[i]] = dagwaardenVanWeek(delen[i], dagen);
  });
  return { totaal, perWeek };
}

/**
 * De uren-map voor ÉÉN week: { naam: { perType: { [type]: uren }, dagen: { [dag]: type } } }.
 *
 * Zelfde aanroepvorm en zelfde route als bouwAfwezigheidTypeMap (data/afwezigheid.js:143),
 * met `regels` erbij om de medewerker en zijn contract bij de naam te vinden. De aanroepers geven
 * daarna nog `vakanties` mee. Die is sinds portie 2 niet meer nodig — het weekgewicht hangt niet
 * af van wie wanneer werkt — en wordt genegeerd; laten staan scheelt vier aanroepers aanpassen,
 * waaronder lib/balans.js.
 *
 * Records zonder ingevuld getal komen hier niet in voor; voor hun dagen geldt bij de lezer het
 * oude pad. Een record MÉT uren zet elke dag die het deze week dekt in `dagen`, ook als het
 * weekdeel 0 is (alleen za+zo, terwijl de rest van het record in andere weken valt). Zo'n dag is
 * betaald door het record, ook al kost hij in Numbers niets, en mag bij de lezer geen 8u via het
 * oude pad meer krijgen.
 *
 * WAAROM DE TYPE-CONTROLE HIER STAAT. Liggen er twee records op dezelfde dag, dan kiest de
 * typeMap het LAATSTE record (bouwAfwezigheidTypeMap) en deze map het laatste record MET uren.
 * Dat kunnen twee verschillende records zijn, bijvoorbeeld ziekte mét uren en daarna verlof
 * zonder. Zolang alleen verlof uren droeg, ving een check op 'verlof' dat op. Met twee typen
 * die uren dragen kan dat niet meer. De controle stond daarom tot nu toe woordelijk in de
 * engine én in balans.js; nu de map weektotalen levert kan ze daar niet meer staan, en doet
 * deze functie hem zelf: een dag komt alleen in de map als de typeMap hem aan hetzelfde type
 * toeschrijft als zijn bronrecord. Een dag die afvalt, valt bij de lezer vanzelf terug op het
 * oude pad. Tegenstrijdige invoer hoort het formulier te voorkomen (overlap-blokkade in
 * VerlofModal); hier wordt er alleen deterministisch mee omgegaan.
 *
 * De verdeling wordt per record één keer gemaakt (verdeelIngevuldeUren rekent altijd over de
 * hele periode), dus welke week er ook gevraagd wordt, de uren per dag zijn dezelfde.
 */
export function bouwAfwezigheidUrenMap(afwezigheid, weeknummer, isoJaar, regels) {
  const typeMaps = bouwAfwezigheidTypeMap(afwezigheid || [], weeknummer, isoJaar);

  // STAP 1 — per naam per dag het LAATSTE record met uren. Deze lus is woordelijk die van
  // vóór de weektotalen, en dat is geen slordigheid maar de kern: liggen er twee records met
  // uren op dezelfde dag, dan WINT HET LAATSTE en telt het eerdere voor die dag niet mee.
  // Pas optellen en dan de dag toewijzen zou ze bij elkaar optellen — gemeten op
  // overlappende records gaf dat tot 16u te veel (test/diag/portie1-equiv.mjs).
  const perDag = {};
  for (const item of afwezigheid || []) {
    const verdeeld = verdeelIngevuldeUren(item, regels);
    if (!verdeeld) continue;
    const dagen = verdeeld.perWeek[`${isoJaar}-${weeknummer}`];
    if (!dagen) continue;
    if (!perDag[item.naam]) perDag[item.naam] = {};
    for (const [dag, uren] of Object.entries(dagen)) perDag[item.naam][dag] = { uren, type: item.type };
  }

  // STAP 2 — de type-controle, en daarna optellen tot het weektotaal per type. De lus loopt
  // ALLE_DAGEN af en niet de sleutels van het object: zo staat de optelvolgorde vast en kan
  // het weektotaal niet per aanroep in de laatste bit verschuiven.
  const result = {};
  for (const [naam, dagen] of Object.entries(perDag)) {
    const typeVanDag = typeMaps[naam] || {};
    for (const dag of ALLE_DAGEN) {
      const bron = dagen[dag];
      if (!bron || !Number.isFinite(bron.uren)) continue;
      if ((typeVanDag[dag] || 'anders') !== bron.type) continue;
      if (!result[naam]) result[naam] = { perType: {}, dagen: {} };
      const r = result[naam];
      r.perType[bron.type] = (r.perType[bron.type] || 0) + bron.uren;
      r.dagen[dag] = bron.type;
    }
  }
  return result;
}
